import React, { useEffect, useState, useCallback } from "react";
import {
  makeStyles,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { toast } from "react-toastify";
import api from "../../services/api";
import { getBackendUrl } from "../../config";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3) },
  codeBlock: {
    backgroundColor: theme.palette.type === "dark" ? "#1a1a2e" : "#f4f4f8",
    borderRadius: 6,
    padding: theme.spacing(1.5),
    fontFamily: "monospace",
    fontSize: 12,
    whiteSpace: "pre-wrap",
    wordBreak: "break-all",
    marginTop: theme.spacing(1),
    overflowX: "auto",
  },
  method: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 4,
    fontWeight: "bold",
    fontSize: 11,
    marginRight: 8,
    color: "#fff",
    flexShrink: 0,
  },
  groupHeader: {
    fontWeight: 700,
    color: "#555",
    borderBottom: "2px solid #eee",
    paddingBottom: 4,
    marginTop: 24,
    marginBottom: 8,
  },
}));

const METHOD_COLORS = { GET: "#10b981", POST: "#3b82f6", PUT: "#f59e0b", DELETE: "#ef4444" };

const GROUP_ORDER = ["Mensajes", "Contactos", "Contactos — IA", "Tickets", "Configuración"];

const SuperAdminApis = () => {
  const classes = useStyles();
  const [examplePhone, setExamplePhone] = useState("549XXXXXXXXXX");
  const [examplePhoneEdit, setExamplePhoneEdit] = useState("");
  const [savingPhone, setSavingPhone] = useState(false);

  // Dominio de la API pública (backend), NO el del frontend. getBackendUrl() lee
  // window.ENV.VITE_BACKEND_URL (lo mismo que usa axios) → correcto también en marca blanca.
  // Fallback al dominio de API de WAIA si no estuviera inyectado.
  const backendUrl = (getBackendUrl() || "https://apiwaia.zzia.info").replace(/\/$/, "");
  const apiToken = "YOUR_API_TOKEN";

  const fetchConfig = useCallback(async () => {
    try {
      const { data } = await api.get("/superadmin/config");
      if (data.examplePhone) {
        setExamplePhone(data.examplePhone);
        setExamplePhoneEdit(data.examplePhone);
      } else {
        setExamplePhoneEdit("549XXXXXXXXXX");
      }
    } catch (_) {}
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const saveExamplePhone = async () => {
    setSavingPhone(true);
    try {
      await api.put("/superadmin/config", { key: "examplePhone", value: examplePhoneEdit });
      setExamplePhone(examplePhoneEdit);
      toast.success("Número de ejemplo guardado");
    } catch (err) {
      toastError(err);
    }
    setSavingPhone(false);
  };

  const ph = examplePhone;
  const baseApi = `${backendUrl}/api`;

  const ENDPOINTS = [

    // ─── GRUPO: Mensajes ───────────────────────────────────────
    {
      group: "Mensajes",
      method: "POST",
      path: "/api/messages/send",
      title: "Enviar mensaje de texto o multimedia",
      description: "Envía un mensaje de texto al contacto. También acepta archivos multimedia via form-data. Alias disponible: /api/send",
      body: `{\n  "contactNumber": "${ph}",\n  "message": "Hola, ¿en qué te puedo ayudar?",\n  "whatsappId": 3,\n  "senderType": "ai"\n}`,
      response: `{ "id": "...", "body": "...", "ticket": { ... } }`,
      curl: `curl -X POST \\\n  -H "x-api-token: ${apiToken}" \\\n  -H "Content-Type: application/json" \\\n  -d '{"contactNumber":"${ph}","message":"Hola, ¿en qué te puedo ayudar?","whatsappId":3}' \\\n  "${baseApi}/messages/send"`,
    },
    {
      group: "Mensajes",
      method: "POST",
      path: "/api/messages/presence",
      title: "Mostrar indicador \"escribiendo...\" al contacto",
      description: "Muestra el indicador \"escribiendo...\" o \"grabando audio...\" al contacto en su teléfono. Pensado para asistentes IA / integraciones que generan una respuesta y quieren mostrar actividad mientras tanto. La presencia EXPIRA (~10s en whaileys/wwebjs, ≤25s en META) — re-llamar el endpoint cada ~8s mientras se genera la respuesta; el mensaje saliente corta el typing solo. Resuelve number→contacto→ticket (open/pending más reciente) scopeado a la empresa del token; si no hay contacto o ticket devuelve status=ignored (nunca rompe el flujo). Gateado por el setting outboundTypingAiEnabled (default activo).",
      body: `{\n  "number": "${ph}",        // teléfono del contacto\n  "state": "composing"       // "composing" | "recording" | "paused"\n}`,
      response: `// OK:\n{ "status": "sent", "ticketId": 123 }\n\n// Sin contacto/ticket (no es error):\n{ "status": "ignored", "reason": "no_contact" }   // o "no_ticket"`,
      curl: `curl -X POST \\\n  -H "x-api-token: ${apiToken}" \\\n  -H "Content-Type: application/json" \\\n  -d '{"number":"${ph}","state":"composing"}' \\\n  "${baseApi}/messages/presence"`,
    },

    // ─── GRUPO: Contactos ──────────────────────────────────────
    {
      group: "Contactos",
      method: "POST",
      path: `/api/contacts/phone/${ph}`,
      title: "Crear o actualizar contacto por teléfono (findOrCreate)",
      description: "Crea el contacto si no existe, o lo actualiza si ya existe. Devuelve created=true si fue creación nueva. name es obligatorio. Útil para pre-crear contactos desde n8n antes de iniciar un flujo.",
      body: `{\n  "name": "Juan",           // obligatorio\n  "lastName": "Pérez",      // opcional\n  "notes": "..."             // opcional\n}`,
      response: `{ "ok": true, "contactId": 1, "created": true, "contact": { ... } }`,
      curl: `curl -X POST \\\n  -H "x-api-token: ${apiToken}" \\\n  -H "Content-Type: application/json" \\\n  -d '{"name":"Juan","notes":"{\\"sucursal\\":\\"Noale Pergamino\\"}"}' \\\n  "${baseApi}/contacts/phone/${ph}"`,
    },
    {
      group: "Contactos",
      method: "GET",
      path: `/api/contacts/phone/${ph}`,
      title: "Obtener datos del contacto por teléfono",
      description: "Devuelve el perfil completo del contacto: nombre, tipo, estado IA, estado de contacto (funnel), ticket activo y más.",
      body: null,
      response: `{\n  "id": 1, "name": "Juan", "number": "${ph}",\n  "contactState": { "id": 3, "name": "Presupuestado" },\n  "ticketId": 123, "ticketStatus": "open",\n  "queueId": 2, "queueName": "Ventas",\n  "assignedUserId": 5, "assignedUserName": "María",\n  "aiStatus": "attending", "humanIntervention": false,\n  "aiBlocked": false, "aiSummary": "...",\n  "contactTypeId": 1, "isProfiled": true, "status": "qualified"\n}`,
      curl: `curl -H "x-api-token: ${apiToken}" \\\n  "${baseApi}/contacts/phone/${ph}"`,
    },
    {
      group: "Contactos — IA",
      method: "PUT",
      path: `/api/contacts/phone/${ph}/profile`,
      title: "Actualizar perfil del contacto (multi-campo)",
      description: `Actualiza el perfil completo del contacto en una sola llamada. Diseñado para que n8n/Martina persista el resultado del perfilado tras una conversación. Todos los campos son opcionales — solo se actualizan los que se envían. clientType acepta: mayorista | minorista | particular | empresa | administracion | comercial. aiStatus: attending | done | null (null también setea humanIntervention=true). isProfiled=true equivale a status=qualified. email acepta valor literal "no_usa".`,
      body: `{\n  "name": "Juan",                  // opcional\n  "lastName": "Pérez",             // opcional\n  "organization": "Mi Empresa",    // opcional (razón social del cliente)\n  "email": "j@mail.com",           // opcional, "no_usa" o email válido\n  "notes": "Notas libres",         // opcional\n  "contactTypeId": 2,              // opcional, null para quitar\n  "isProfiled": true,              // opcional, mapea a status=qualified\n  "clientType": "mayorista",       // opcional, enum extendido\n  "aiSummary": "Resumen IA...",    // opcional, consolida endpoint legacy /summary\n  "aiStatus": "done"               // opcional, attending | done | null\n}`,
      response: `{ "ok": true, "contactId": 1, "updated": ["clientType","aiSummary"], "contact": { ... } }`,
      curl: `curl -X PUT \\\n  -H "x-api-token: ${apiToken}" \\\n  -H "Content-Type: application/json" \\\n  -d '{"clientType":"mayorista","aiSummary":"Cliente interesado en volumen mayorista.","isProfiled":true,"aiStatus":"done"}' \\\n  "${baseApi}/contacts/phone/${ph}/profile"`,
    },
    {
      group: "Contactos — IA",
      method: "PUT",
      path: `/api/contacts/phone/${ph}/fields`,
      title: "Actualizar campos personalizados (Información adicional)",
      description: `Upsert de campos personalizados que se muestran en el bloque "Información adicional" del modal de Editar contacto. Semántica: upsert parcial por nombre. Si el campo ya existe en el contacto actualiza su valor; si no existe lo crea. NO borra campos que no se envían. Pensado para que n8n/Martina persista atributos específicos del negocio (transporte, CUIT, código de cliente, zona, etc.) sin necesidad de columnas dedicadas en Contacts. Estos campos llegan automáticamente en próximas interacciones vía el campo contactExtraInfo del webhook.`,
      body: `{\n  "fields": [\n    { "name": "Transporte", "value": "Andreani" },\n    { "name": "CUIT", "value": "30-12345678-9" }\n  ]\n}`,
      response: `{\n  "ok": true,\n  "contactId": 1,\n  "upserted": [\n    { "name": "Transporte", "value": "Andreani", "action": "created" },\n    { "name": "CUIT", "value": "30-12345678-9", "action": "updated" }\n  ],\n  "contact": { ... }\n}`,
      curl: `curl -X PUT \\\n  -H "x-api-token: ${apiToken}" \\\n  -H "Content-Type: application/json" \\\n  -d '{"fields":[{"name":"Transporte","value":"Andreani"},{"name":"CUIT","value":"30-12345678-9"}]}' \\\n  "${baseApi}/contacts/phone/${ph}/fields"`,
    },
    {
      group: "Contactos",
      method: "GET",
      path: `/api/contacts/${ph}/tags`,
      title: "Listar tags del contacto",
      description: "Devuelve todos los tags asignados al contacto. El parámetro puede ser el ID numérico del contacto (corto, < 10 dígitos) o el número de teléfono E.164 sin '+'.",
      body: null,
      response: `{\n  "contact": { "id": 123, "name": "Juan Perez", "number": "${ph}" },\n  "tags": [\n    { "id": 5, "name": "Cliente VIP", "color": "#28a745" },\n    { "id": 8, "name": "Comprador", "color": "#007bff" }\n  ]\n}`,
      curl: `curl -H "x-api-token: ${apiToken}" \\\n  "${baseApi}/contacts/${ph}/tags"`,
    },
    {
      group: "Contactos",
      method: "POST",
      path: `/api/contacts/${ph}/tags`,
      title: "Asignar tags al contacto (single o batch)",
      description: "Asigna uno o varios tags al contacto. Idempotente: si el tag ya estaba asignado devuelve status='alreadyPresent' sin error. createIfMissing=true (default) crea el tag si no existe. En batch devuelve HTTP 207 si algún item falló. El parámetro puede ser ID numérico o teléfono E.164.",
      body: `// Single:\n{\n  "tagName": "Cliente VIP",\n  "color": "#28a745",      // opcional, solo si crea\n  "createIfMissing": true  // default true\n}\n\n// Batch:\n{\n  "tags": [\n    { "name": "VIP", "color": "#28a745" },\n    { "name": "Followup" },\n    { "id": 7 }\n  ],\n  "createIfMissing": true\n}`,
      response: `{\n  "contact": { "id": 123, "name": "Juan Perez", "number": "${ph}" },\n  "results": [\n    { "tag": { "id": 5, "name": "VIP", "color": "#28a745" }, "status": "assigned" },\n    { "tag": { "id": 8, "name": "Followup", "color": "#6c757d" }, "status": "alreadyPresent" }\n  ],\n  "tags": [ /* tags actuales del contacto */ ]\n}`,
      curl: `# Single:\ncurl -X POST \\\n  -H "x-api-token: ${apiToken}" \\\n  -H "Content-Type: application/json" \\\n  -d '{"tagName":"API_TEST","color":"#ff9900"}' \\\n  "${baseApi}/contacts/${ph}/tags"\n\n# Batch:\ncurl -X POST \\\n  -H "x-api-token: ${apiToken}" \\\n  -H "Content-Type: application/json" \\\n  -d '{"tags":[{"name":"VIP"},{"name":"Followup"}]}' \\\n  "${baseApi}/contacts/${ph}/tags"`,
    },
    {
      group: "Contactos",
      method: "DELETE",
      path: `/api/contacts/${ph}/tags/:tagIdOrName`,
      title: "Quitar tag del contacto",
      description: "Elimina la asignación de un tag al contacto. No borra el tag de la empresa, solo la relación. Idempotente: si el tag no estaba asignado devuelve status='notPresent' con HTTP 200. :tagIdOrName puede ser el ID numérico del tag o su nombre (case-insensitive).",
      body: null,
      response: `{\n  "contact": { "id": 123, "name": "Juan Perez", "number": "${ph}" },\n  "removed": { "id": 5, "name": "API_TEST" },\n  "status": "removed",  // o "notPresent"\n  "tags": [ /* tags actuales restantes */ ]\n}`,
      curl: `curl -X DELETE \\\n  -H "x-api-token: ${apiToken}" \\\n  "${baseApi}/contacts/${ph}/tags/API_TEST"`,
    },
    {
      group: "Contactos",
      method: "PUT",
      path: "/api/contacts/:contactId/state",
      title: "Cambiar estado del contacto en el funnel de ventas",
      description: "Cambia el estado del contacto (Nuevo, Asesorado, Presupuestado, Ganado, Perdido o estados custom). Usar el ID numérico del estado. Estados del sistema: 1=Nuevo, 2=Asesorado, 3=Presupuestado, 4=Ganado, 5=Perdido. Si el destino es Perdido se puede mandar el motivo: lostReasonId (ID de un motivo de la empresa; si no existe responde 404 ERR_LOST_REASON_NOT_FOUND) y/o lostReasonText (texto libre, máx. 255). Con otro destino el motivo se ignora. Cada cambio queda en el historial del contacto con origen API.",
      body: `{\n  "stateId": 2,\n  "lostReasonId": 7,        // opcional, solo con destino Perdido\n  "lostReasonText": "Compró en otro lado"  // opcional\n}`,
      response: `{ "success": true, "contactId": 123, "stateId": 2 }`,
      curl: `curl -X PUT \\\n  -H "x-api-token: ${apiToken}" \\\n  -H "Content-Type: application/json" \\\n  -d '{"stateId":2}' \\\n  "${baseApi}/contacts/123/state"`,
    },
    {
      group: "Contactos",
      method: "PUT",
      path: `/api/contacts/phone/${ph}/type`,
      title: "Cambiar tipo de contacto (clasificación comercial)",
      description: "Asigna un tipo de contacto al número dado (ej: Mayorista, Minorista, Particular). El tipo debe existir en la empresa.",
      body: `{\n  "typeId": 1\n}`,
      response: `{ "id": 1, "contactTypeId": 1, ... }`,
      curl: `curl -X PUT \\\n  -H "x-api-token: ${apiToken}" \\\n  -H "Content-Type: application/json" \\\n  -d '{"typeId":1}' \\\n  "${baseApi}/contacts/phone/${ph}/type"`,
    },

    // ─── GRUPO: Contactos — IA ─────────────────────────────────
    {
      group: "Contactos — IA",
      method: "PUT",
      path: `/api/contacts/phone/${ph}/ai-status`,
      title: "Cambiar estado de la IA para el contacto",
      description: "Controla si la IA está activa, en pausa o atendiendo al contacto. Usado por n8n al inicio y fin de cada flujo de IA. Valores: \"attending\" | \"done\" | null (resetear — también activa humanIntervention=true).",
      body: `{\n  "aiStatus": "attending"   // "attending" | "done" | null\n}`,
      response: `{ "id": 1, "aiStatus": "attending", ... }`,
      curl: `curl -X PUT \\\n  -H "x-api-token: ${apiToken}" \\\n  -H "Content-Type: application/json" \\\n  -d '{"aiStatus":"attending"}' \\\n  "${baseApi}/contacts/phone/${ph}/ai-status"`,
    },
    {
      group: "Contactos — IA",
      method: "PUT",
      path: `/api/contacts/phone/${ph}/intervention`,
      title: "Activar o desactivar intervención humana",
      description: "Marca si un asesor humano tomó control del chat. Cuando humanIntervention=true, la IA deja de responder aunque siga recibiendo el webhook. aiBlocked=true bloquea la IA permanentemente.",
      body: `{\n  "humanIntervention": true, // o "disableBot" como alias\n  "aiBlocked": false\n}`,
      response: `{ "id": 1, "humanIntervention": true, ... }`,
      curl: `curl -X PUT \\\n  -H "x-api-token: ${apiToken}" \\\n  -H "Content-Type: application/json" \\\n  -d '{"humanIntervention":true}' \\\n  "${baseApi}/contacts/phone/${ph}/intervention"`,
    },
    {
      group: "Contactos — IA",
      method: "PUT",
      path: `/api/contacts/phone/${ph}/summary`,
      title: "Guardar resumen de conversación generado por IA",
      description: "Almacena un resumen de la conversación como nota interna visible en el chat. Aparece con fondo amarillo igual que las notas del sistema.",
      body: `{\n  "summary": "El cliente consultó por stock de perfiles de aluminio 20x20. Interesado en cantidad mayorista."\n}`,
      response: `{ "success": true }`,
      curl: `curl -X PUT \\\n  -H "x-api-token: ${apiToken}" \\\n  -H "Content-Type: application/json" \\\n  -d '{"summary":"El cliente consultó frecuentemente sobre precios mayoristas."}' \\\n  "${baseApi}/contacts/phone/${ph}/summary"`,
      deprecated: true,
      deprecatedNote: `Reemplazado por PUT /api/contacts/phone/:phone/profile (campo aiSummary). Mantenido activo por compatibilidad con workflows existentes.`,
    },
    {
      group: "Contactos — IA",
      method: "PUT",
      path: `/api/contacts/phone/${ph}/status`,
      title: "Actualizar estado de calificación del contacto",
      description: "Marca si el contacto fue perfilado por la IA (contactIsProfiled). Usado por la Secretaria IA al terminar el flujo de perfilado. Valores: \"pending\" | \"qualified\" | \"disqualified\".",
      body: `{\n  "status": "qualified"     // "pending" | "qualified" | "disqualified"\n}`,
      response: `{ "id": 1, "status": "qualified", ... }`,
      curl: `curl -X PUT \\\n  -H "x-api-token: ${apiToken}" \\\n  -H "Content-Type: application/json" \\\n  -d '{"status":"qualified"}' \\\n  "${baseApi}/contacts/phone/${ph}/status"`,
    },
    {
      group: "Contactos — IA",
      method: "PUT",
      path: `/api/contacts/phone/${ph}/ai-assistant`,
      title: "Identificar el asistente IA que atiende al contacto",
      description: "Marca qué asistente IA está atendiendo al contacto. Si el asistente no existe en el catálogo de la empresa, se crea automáticamente. Si el contacto ya tenía otro asistente asignado, lo sobrescribe (semántica: último asistente activo). El match contra el catálogo es insensible a mayúsculas/minúsculas y acentos; el nombre se guarda tal como llega la primera vez. El contacto debe existir (si no, devuelve 404; no se crea). Pensado para que n8n registre el asistente al decidir quién responde el primer mensaje.",
      body: `{\n  "name": "Sofia"\n}`,
      response: `{\n  "ok": true,\n  "contact": { "id": 123, "number": "${ph}", "name": "Juan", "aiAssistantId": 1, "aiAssistantName": "Sofia" },\n  "assistant": { "id": 1, "name": "Sofia", "isNew": true }\n}`,
      curl: `curl -X PUT \\\n  -H "x-api-token: ${apiToken}" \\\n  -H "Content-Type: application/json" \\\n  -d '{"name":"Sofia"}' \\\n  "${baseApi}/contacts/phone/${ph}/ai-assistant"`,
    },
    {
      group: "Contactos — IA",
      method: "GET",
      path: `/api/ai-assistants`,
      title: "Listar catálogo de asistentes IA de la empresa",
      description: "Devuelve todos los asistentes IA registrados para la empresa (el catálogo crece solo a medida que se identifican asistentes vía PUT). Útil para poblar dropdowns en módulos de estadísticas o filtros. Ordenado por nombre.",
      response: `{\n  "ok": true,\n  "count": 2,\n  "assistants": [\n    { "id": 1, "name": "Sofia", "firstSeenAt": "2026-06-19T22:24:23.000Z", "lastUsedAt": "2026-06-19T22:24:23.000Z" },\n    { "id": 2, "name": "Vendedor Premium", "firstSeenAt": "...", "lastUsedAt": "..." }\n  ]\n}`,
      curl: `curl -H "x-api-token: ${apiToken}" \\\n  "${baseApi}/ai-assistants"`,
    },

    // ─── GRUPO: Tickets ────────────────────────────────────────
    {
      group: "Tickets",
      method: "POST",
      path: `/api/contacts/phone/${ph}/assign`,
      title: "Asignar cola o agente al ticket activo del contacto",
      description: "Mueve el ticket activo del contacto a una cola específica y/o lo asigna a un agente. Útil para derivar desde n8n según el resultado del perfilado. queueName hace búsqueda case-insensitive. userId null desasigna el agente.",
      body: `{\n  "queueId": 1,             // o "queueName": "Ventas"\n  "userId": 5               // opcional, null para desasignar\n}`,
      response: `{ "id": 123, "queueId": 1, "userId": 5, ... }`,
      curl: `curl -X POST \\\n  -H "x-api-token: ${apiToken}" \\\n  -H "Content-Type: application/json" \\\n  -d '{"queueName":"Ventas","userId":5}' \\\n  "${baseApi}/contacts/phone/${ph}/assign"`,
    },
    {
      group: "Tickets",
      method: "POST",
      path: `/api/contacts/phone/${ph}/internal-note`,
      title: "Agregar nota interna al ticket del contacto",
      description: "Inserta una nota interna en el chat del contacto. Solo visible para los asesores, no se envía al cliente. Aparece con fondo amarillo.",
      body: `{\n  "note": "Cliente consultó fuera de horario. Llamar mañana a las 10hs."\n}`,
      response: `{ "success": true, "messageId": "..." }`,
      curl: `curl -X POST \\\n  -H "x-api-token: ${apiToken}" \\\n  -H "Content-Type: application/json" \\\n  -d '{"note":"El cliente llamó ayer para confirmar el pedido."}' \\\n  "${baseApi}/contacts/phone/${ph}/internal-note"`,
    },

    // ─── GRUPO: Configuración ──────────────────────────────────
    {
      group: "Configuración",
      method: "GET",
      path: "/api/queues",
      title: "Listar colas disponibles de la empresa",
      description: "Devuelve todas las colas configuradas en la empresa. Usar los IDs en el endpoint de asignación. Agregar ?includeColor=true para incluir el color de cada cola.",
      body: null,
      response: `[{ "id": 1, "name": "Ventas", "color": "#FF0000" }, ...]`,
      curl: `curl -H "x-api-token: ${apiToken}" \\\n  "${baseApi}/queues?includeColor=true"`,
    },
    {
      group: "Configuración",
      method: "GET",
      path: "/api/whatsapp",
      title: "Listar conexiones WhatsApp de la empresa",
      description: "Devuelve todas las conexiones WhatsApp configuradas con su estado actual. Usar los IDs en el endpoint de envío de mensajes.",
      body: null,
      response: `[{ "id": 3, "name": "Ventas", "status": "CONNECTED", "number": "549351...", "isDefault": true }, ...]`,
      curl: `curl -H "x-api-token: ${apiToken}" \\\n  "${baseApi}/whatsapp"`,
    },
    {
      group: "Configuración",
      method: "GET",
      path: "/api/settings/business-hours-api",
      title: "Obtener horario de atención de la empresa",
      description: "Devuelve el horario de atención configurado. Útil para que n8n decida si responder o informar que está fuera de horario.",
      body: null,
      response: `{ "enabled": true, "monday": { "open": "09:00", "close": "18:00", "enabled": true }, ... }`,
      curl: `curl -H "x-api-token: ${apiToken}" \\\n  "${baseApi}/settings/business-hours-api"`,
    },
    {
      group: "Configuración",
      method: "PUT",
      path: "/api/settings/business-hours-api",
      title: "Actualizar horario de atención de la empresa",
      description: "Modifica el horario de atención. Requiere enviar la estructura completa del horario con todos los días.",
      body: `{\n  "enabled": true,\n  "monday":    { "open": "09:00", "close": "18:00", "enabled": true },\n  "tuesday":   { "open": "09:00", "close": "18:00", "enabled": true },\n  "wednesday": { "open": "09:00", "close": "18:00", "enabled": true },\n  "thursday":  { "open": "09:00", "close": "18:00", "enabled": true },\n  "friday":    { "open": "09:00", "close": "18:00", "enabled": true },\n  "saturday":  { "open": "09:00", "close": "13:00", "enabled": false },\n  "sunday":    { "open": "09:00", "close": "13:00", "enabled": false }\n}`,
      response: `{ "success": true }`,
      curl: `curl -X PUT \\\n  -H "x-api-token: ${apiToken}" \\\n  -H "Content-Type: application/json" \\\n  -d '{"enabled":true,"monday":{"open":"09:00","close":"18:00","enabled":true},...}' \\\n  "${baseApi}/settings/business-hours-api"`,
    },
  ];

  // Group endpoints preserving GROUP_ORDER
  const grouped = GROUP_ORDER.map(groupName => ({
    name: groupName,
    endpoints: ENDPOINTS.filter(ep => ep.group === groupName),
  })).filter(g => g.endpoints.length > 0);

  return (
    <div className={classes.root}>
      <Typography variant="h5" gutterBottom>APIs del sistema</Typography>
      <Paper style={{ padding: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <TextField
            label="Número de teléfono de ejemplo"
            size="small"
            variant="outlined"
            value={examplePhoneEdit}
            onChange={e => setExamplePhoneEdit(e.target.value)}
            style={{ minWidth: 220 }}
            placeholder="549XXXXXXXXXX"
          />
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={saveExamplePhone}
            disabled={savingPhone || !examplePhoneEdit}
          >
            {savingPhone ? <CircularProgress size={16} /> : "Guardar"}
          </Button>
        </div>
        <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 12 }}>
          Autenticación: header <code>x-api-token</code> con el token de la empresa (ver Configuración → API Token).
        </Typography>
        <Divider style={{ marginBottom: 4 }} />

        {grouped.map(group => (
          <div key={group.name}>
            <Typography variant="h6" className={classes.groupHeader}>
              {group.name}
            </Typography>
            {group.endpoints.map((ep, i) => (
              <Accordion key={i} elevation={0} style={{ border: `1px solid ${ep.deprecated ? "#f57c00" : "#e0e0e0"}`, marginBottom: 6, borderRadius: 6 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} style={{ alignItems: "center" }}>
                  <span className={classes.method} style={{ backgroundColor: METHOD_COLORS[ep.method] || "#666" }}>
                    {ep.method}
                  </span>
                  <Typography style={{ fontFamily: "monospace", fontSize: 13 }}>{ep.path}</Typography>
                  {ep.deprecated && (
                    <span style={{
                      marginLeft: 8,
                      padding: "2px 8px",
                      backgroundColor: "#d32f2f",
                      color: "#fff",
                      fontSize: "0.7em",
                      fontWeight: "bold",
                      borderRadius: 4,
                      letterSpacing: "0.5px",
                      flexShrink: 0,
                    }}>
                      DEPRECATED
                    </span>
                  )}
                </AccordionSummary>
                <AccordionDetails style={{ flexDirection: "column" }}>
                  {ep.deprecated && ep.deprecatedNote && (
                    <div style={{
                      padding: "10px 14px",
                      marginBottom: 12,
                      backgroundColor: "#fff3e0",
                      borderLeft: "4px solid #f57c00",
                      borderRadius: 4,
                      fontSize: "0.9em",
                    }}>
                      <strong>Endpoint deprecated:</strong> {ep.deprecatedNote}
                    </div>
                  )}
                  <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 4 }}>
                    {ep.title}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                    {ep.description}
                  </Typography>
                  {ep.body && (
                    <>
                      <Typography variant="caption" color="textSecondary">Body (JSON):</Typography>
                      <div className={classes.codeBlock}>{ep.body}</div>
                    </>
                  )}
                  {ep.response && (
                    <>
                      <Typography variant="caption" color="textSecondary" style={{ marginTop: 8 }}>Respuesta:</Typography>
                      <div className={classes.codeBlock}>{ep.response}</div>
                    </>
                  )}
                  <Typography variant="caption" color="textSecondary" style={{ marginTop: ep.body || ep.response ? 8 : 0 }}>
                    Ejemplo curl:
                  </Typography>
                  <div className={classes.codeBlock}>{ep.curl}</div>
                </AccordionDetails>
              </Accordion>
            ))}
          </div>
        ))}
      </Paper>
    </div>
  );
};

export default SuperAdminApis;
